import { useQuery } from '@tanstack/react-query'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { authFetchJson } from '@/shared/lib/auth-fetch'

const ACTIVE_WORK_SCOPES_STALE_MS = 5 * 60 * 1000

const EMPTY_ACTIVE_WORK_SCOPES: Record<string, boolean> = {}

async function fetchActiveWorkScopes(regionId: number): Promise<Record<string, boolean>> {
    const res = await authFetchJson<Record<string, boolean>>(`/work-scopes/active-for-region?regionId=${regionId}`)
    const activeScopes = (res as any)?.success === false ? {} : ((res as any)?.success ? (res as any).data : res)
    return activeScopes && typeof activeScopes === 'object' ? activeScopes : {}
}

/**
 * Active work scopes for the current region.
 * Cached via React Query so the many consumers (home details, list, reports) share one request.
 */
export function useActiveWorkScopes() {
    const { regionId } = useRegionId()

    const query = useQuery({
        queryKey: ['work-scopes', 'active-for-region', regionId ?? ''],
        queryFn: () => fetchActiveWorkScopes(regionId!),
        enabled: regionId != null,
        staleTime: ACTIVE_WORK_SCOPES_STALE_MS,
        refetchOnWindowFocus: false,
    })

    return {
        // Default to all active on error/loading so sections and statuses aren't hidden.
        activeWorkScopes: query.data ?? EMPTY_ACTIVE_WORK_SCOPES,
        loading: regionId != null && query.isLoading,
        error: query.error instanceof Error ? query.error.message : null,
    }
}
